import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { IOfferItem } from "../../types/Offer";
import { OfferSliceInterface } from "../../types/reduxSlices";
import { OfferStepsEnum, OfferTypesEnum } from "../../utils/constants/CommonConstants";

const initialState: OfferSliceInterface = {
  offerStep: OfferStepsEnum.initial,
  offerItems: [],
  offerType: "",
  disableDiscountForAll: false,
  disableFreeQuantityForAll: false,
  offerDetails: {
    expirationDate: "",
    arabicName: "",
    englishName: "",
    totalPrice: 0,
    totalPriceAfterDiscounts: 0,
  },
  selectedOfferTableTabFilter: "",
  clearQuantitiesAndDiscountAndNetPriceInputs: false,
};

const offerSlice = createSlice({
  name: "offer",
  initialState,
  reducers: {
    updateOfferStep: (state, action: PayloadAction<string>) => {
      state.offerStep = action.payload;
    },

    nextStep: (state) => {
      if (state.offerStep === OfferStepsEnum.initial) {
        state.offerStep = OfferStepsEnum.chooseQuantity;
      } else if (state.offerStep === OfferStepsEnum.chooseQuantity) {
        const hasIncompleteItem = state.offerItems.some(
          (item) =>
            (item.Quantity ?? 0) === 0 &&
            (item.Discount ?? 0) === 0 &&
            (item.FreeQuantity ?? 0) === 0,
        );

        if (hasIncompleteItem) {
          return; // Block moving forward if any item is incomplete
        }

        if (state.offerItems.length > 1) {
          // UNCOMMENT THIS IF WE WANT TO ALLOW MOVING TO OFFER DETAILS IF IT'S A PACKAGE NORMAL (ONLY QUANTITY MATTERS)

          // Handle case for packageNormal (only Quantity matters)
          // const isPackageNormal = state.offerItems.some(
          //   (item) =>
          //     (item.Quantity ?? 0) > 0 && // Quantity must be greater than 0
          //     (item.FreeQuantity ?? 0) === 0 && // No FreeQuantity in packageNormal
          //     (item.Discount ?? 0) === 0, // No Discount in packageNormal
          // );

          // if (isPackageNormal) {
          //   // Proceed to offerDetails if it's a valid packageNormal
          //   state.offerStep = OfferStepsEnum.offerDetails;
          //   return;
          // }


          //  And package normal at the same time 
          const hasQuantity = state.offerItems.some(
            (item) => (item.Quantity ?? 0) > 0
          );

          const hasBenefit = state.offerItems.some(
            (item) =>
              (item.FreeQuantity ?? 0) > 0 ||
              (item.Discount && item.Quantity! > 0))


          const canProceed = hasQuantity && hasBenefit;

          if (canProceed) {
            state.offerStep = OfferStepsEnum.offerDetails;
            return;
          }



          //   (item) =>
          //     (item.Quantity! > 0 && item.FreeQuantity! > 0) && // Quantity must be greater than 0
          //     (item.FreeQuantity ?? 0) > 0 && // FreeQuantity must be greater than 0
          //     (item.Discount ?? 0) === 0, // No Discount in packageQuantity
          // );
          // if (isPackageQuantity) {
          //   state.offerStep = OfferStepsEnum.offerDetails;
          //   return;
          // }

          // Handle case for Discount (at least one item has Discount)
          const isPackageDiscount = state.offerItems.some(
            (item) =>
              (item.Discount ?? 0) > 0 && // At least one item has a Discount
              (item.Quantity ?? 0) > 0, // And it has a valid Quantity
          );
          if (isPackageDiscount) {
            state.offerStep = OfferStepsEnum.offerDetails;
            return;
          }

          // Block if any item is missing both Discount and FreeQuantity, or has a Discount/FreeQuantity but no Quantity
          const hasInvalidItem = state.offerItems.some(
            (item) =>
              // If the item has no Discount or FreeQuantity and no Quantity
              ((item.Discount ?? 0) === 0 && (item.FreeQuantity ?? 0) === 0) ||
              // If the item has Discount or FreeQuantity but no Quantity
              (((item.Discount ?? 0) > 0 || (item.FreeQuantity ?? 0) > 0) &&
                (item.Quantity ?? 0) === 0),
          );

          if (hasInvalidItem) {
            return; // Block if any item violates the Quantity-Discount-FreeQuantity rule
          }

          // Proceed to offerDetails if all items have valid relationships
          state.offerStep = OfferStepsEnum.offerDetails;
        } else if (state.offerItems.length === 1) {
          // If there is only 1 item in offerItems
          const item = state.offerItems[0];
          const hasValidQuantity = (item.Quantity ?? 0) > 0;
          const hasValidDiscountOrFreeQuantity =
            (item.Discount ?? 0) > 0 || (item.FreeQuantity ?? 0) > 0;

          if (hasValidQuantity && hasValidDiscountOrFreeQuantity) {
            state.offerStep = OfferStepsEnum.offerDetails;
          } else {
            return; // Block if the conditions are not met
          }
        }
      }

      // Update offerType based on the updated items
      offerSlice.caseReducers.updateOfferType(state);
    },
    previousStep: (state) => {
      if (state.offerStep === OfferStepsEnum.offerDetails) {
        state.offerStep = OfferStepsEnum.chooseQuantity;
      } else if (state.offerStep === OfferStepsEnum.chooseQuantity) {
        state.offerStep = OfferStepsEnum.initial;
      }
      // Update offerType after going back
      offerSlice.caseReducers.updateOfferType(state);
    },

    addOfferItem: (state, action: PayloadAction<Partial<IOfferItem>>) => {
      const { offerItems } = action.payload;
      if (offerItems !== undefined) {
        state.offerItems = offerItems;
      }
    },
    updateOfferItemDetails: (
      state,
      action: PayloadAction<{
        Id: number;
        Quantity?: number | null;
        FreeQuantity?: number | null;
        Discount?: number | null;
        NetPrice: number;
      }>,
    ) => {
      const { Id, Quantity, FreeQuantity, Discount, NetPrice } = action.payload;
      const offerItem = state.offerItems.find((item) => item.Id === Id);

      if (!offerItem) {
        throw new Error("Offer item not found");
      }

      // Update Quantity, FreeQuantity, Discount, and NetPrice for the selected item
      offerItem.Quantity =
        Quantity !== undefined && Quantity !== null ? Quantity : null;
      offerItem.NetPrice = NetPrice;

      // Handle FreeQuantity and Discount cases
      if (
        FreeQuantity !== undefined &&
        FreeQuantity !== null &&
        FreeQuantity >= 0
      ) {
        offerItem.FreeQuantity = FreeQuantity; // Ensure it's a valid number
        offerItem.Discount = null; // Reset Discount if FreeQuantity is set
      } else if (
        Discount !== undefined &&
        Discount !== null &&
        Discount >= 0 &&
        Discount <= 100
      ) {
        offerItem.Discount = Discount; // Ensure it's a valid number between 0 and 100
        offerItem.FreeQuantity = null; // Reset FreeQuantity if Discount is set
      } else {
        // If both FreeQuantity and Discount are invalid or empty, reset both
        offerItem.FreeQuantity = null;
        offerItem.Discount = null;
      }

      // Check if any item has FreeQuantity or Discount
      const hasFreeQuantity = state.offerItems.some(
        (item) => (item.FreeQuantity ?? 0) > 0,
      );
      const hasDiscount = state.offerItems.some(
        (item) => (item.Discount ?? 0) > 0,
      );

      state.disableFreeQuantityForAll = hasDiscount;
      state.disableDiscountForAll = hasFreeQuantity;

      // Update offerType after item update
      offerSlice.caseReducers.updateOfferType(state);

      // Recalculate totalPrice after updating item details
      offerSlice.caseReducers.calculateTotalPrice(state);
    },
    handleClearQuantitiesAndDiscountAndNetPriceInputs: (state) => {
      state.offerItems.forEach((offerItem) => {
        offerItem.Quantity = null;
        offerItem.FreeQuantity = null;
        offerItem.Discount = null;
      });

      state.disableFreeQuantityForAll = false;
      state.disableDiscountForAll = false;

      offerSlice.caseReducers.updateOfferType(state);
      offerSlice.caseReducers.calculateTotalPrice(state);
    },
    updateOfferType: (state) => {
      const { offerItems } = state;

      if (offerItems.length === 1) {
        const item = offerItems[0];

        if ((item.FreeQuantity ?? 0) > 0 && (item.Discount ?? 0) === 0) {
          state.offerType = OfferTypesEnum.Quantity;
        } else if ((item.Discount ?? 0) > 0 && (item.FreeQuantity ?? 0) === 0) {
          state.offerType = OfferTypesEnum.Discount;
        }
      } else if (offerItems.length > 1) {
        // Check if all items only have Quantity
        const hasOnlyQuantities = offerItems.every(
          (item) =>
            (item.Quantity ?? 0) > 0 && // All items must have Quantity > 0
            (item.FreeQuantity ?? 0) === 0 &&
            (item.Discount ?? 0) === 0,
        );

        // if (hasOnlyQuantities) {
        //   state.offerType = "packageNormal";
        // } else 
        if (offerItems.some((item) => (item.FreeQuantity ?? 0) > 0)) {
          state.offerType = "packageQuantity";
        } else if (offerItems.some((item) => (item.Discount ?? 0) > 0)) {
          state.offerType = "packageDiscount";
        }
      }
    },
    calculateTotalPrice: (state) => {
      // Initialize totalPrice and totalPriceAfterDiscounts to 0
      let totalPrice = 0;
      let totalPriceAfterDiscounts = 0;

      // Iterate through all offerItems and calculate their total price
      state.offerItems.forEach((item) => {
        const quantity = item.Quantity ?? 0; // Ensure Quantity is a number
        const netPrice = item.NetPrice ?? 0; // Ensure NetPrice is a number
        const itemTotal = quantity * netPrice; // Calculate the item total

        totalPrice += itemTotal; // Add to total price before discounts

        if (item.Discount !== null && item.Discount >= 0) {
          // Apply discount if present, including 100% discount
          totalPriceAfterDiscounts += itemTotal * (1 - item.Discount / 100);
        } else {
          totalPriceAfterDiscounts += itemTotal;
        }
      });

      // If all discounts are zero, set totalPriceAfterDiscounts to 0
      const allDiscountsZero = state.offerItems.every(
        (item) => (item.Discount ?? 0) === 0,
      );
      if (allDiscountsZero) {
        totalPriceAfterDiscounts = 0;
      }

      // Update the totalPrice and totalPriceAfterDiscounts in the state
      state.offerDetails.totalPrice = totalPrice;
      state.offerDetails.totalPriceAfterDiscounts = totalPriceAfterDiscounts;
    },
    // Clear both total prices
    clearTotalPrices: (state) => {
      state.offerDetails.totalPrice = 0;
      state.offerDetails.totalPriceAfterDiscounts = 0;
    },
    clearOfferType: (state) => {
      state.offerType = "";
    },
    clearOfferItems: (state) => {
      state.offerItems = [];
    },
    setOfferDetails: (
      state,
      action: PayloadAction<
        Partial<{
          expirationDate: string;
          arabicName: string;
          englishName: string;
        }>
      >,
    ) => {
      const { expirationDate, arabicName, englishName } = action.payload;

      if (expirationDate !== undefined) {
        state.offerDetails.expirationDate = expirationDate;
      }
      if (arabicName !== undefined) {
        state.offerDetails.arabicName = arabicName;
      }
      if (englishName !== undefined) {
        state.offerDetails.englishName = englishName;
      }

      offerSlice.caseReducers.calculateTotalPrice(state);
    },
    setSelectedTableTabFilter: (state, action: PayloadAction<string>) => {
      state.selectedOfferTableTabFilter = action.payload;
    },
  },
});

export const {
  addOfferItem,
  updateOfferItemDetails,
  setOfferDetails,
  nextStep,
  previousStep,
  updateOfferStep,
  clearOfferType,
  updateOfferType,
  clearTotalPrices,
  setSelectedTableTabFilter,
  handleClearQuantitiesAndDiscountAndNetPriceInputs,
  calculateTotalPrice,
  clearOfferItems,
} = offerSlice.actions;

export default offerSlice.reducer;
